import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useToast } from '../ui/Toast';
import { visitReportsApi } from '../../services/visitReportsApi';
import type { VisitReportSummary } from '../../types/visitReport';

interface VisitReportDraftsBannerProps {
    drafts: VisitReportSummary[];
    onResume: (draftId: string) => void;
    onDraftsChange: (next: VisitReportSummary[]) => void;
    /** Label shown in the amber banner. Defaults to "drafts". */
    noun?: string;
}

/**
 * Amber banner + expandable list + delete-with-confirm for unfinished visit-report drafts.
 * Reused by the standalone /visit-report page and the Customer 360 "Visit Report" tab.
 */
export function VisitReportDraftsBanner({
    drafts,
    onResume,
    onDraftsChange,
    noun = 'draft',
}: VisitReportDraftsBannerProps) {
    const { toast } = useToast();
    const [expanded, setExpanded] = useState(false);
    const [draftToDelete, setDraftToDelete] = useState<VisitReportSummary | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    if (drafts.length === 0) return null;

    const handleConfirmDelete = async () => {
        if (!draftToDelete) return;
        setIsDeleting(true);
        try {
            await visitReportsApi.discardDraft(draftToDelete.id);
            onDraftsChange(drafts.filter(d => d.id !== draftToDelete.id));
            toast('Draft deleted', 'success');
            setDraftToDelete(null);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete draft';
            toast(message, 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            {!expanded && (
                <div className="border border-amber-200 bg-amber-50 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-sm text-amber-900">
                            You have <strong>{drafts.length}</strong> unfinished {noun}{drafts.length === 1 ? '' : 's'}.
                        </div>
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            className="text-xs text-amber-800 underline"
                        >
                            Show
                        </button>
                    </div>
                </div>
            )}

            {expanded && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-gray-700">Your drafts</h2>
                        <button
                            type="button"
                            onClick={() => setExpanded(false)}
                            className="text-xs text-gray-500 underline"
                        >
                            Hide
                        </button>
                    </div>
                    {drafts.map(d => (
                        <div
                            key={d.id}
                            className="flex items-stretch bg-white border border-gray-200 rounded-xl hover:border-blue-400 overflow-hidden"
                        >
                            <button
                                type="button"
                                onClick={() => onResume(d.id)}
                                className="flex-1 min-w-0 text-left px-3 py-3 hover:bg-blue-50"
                            >
                                <div className="text-sm font-medium text-gray-800 truncate">
                                    {d.companyName || d.customerName || '(no customer)'}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {d.visitDate} · updated {new Date(d.updatedAt).toLocaleString('de-CH')}
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setDraftToDelete(d)}
                                aria-label="Delete draft"
                                title="Delete draft"
                                className="px-3 text-gray-400 hover:text-red-600 hover:bg-red-50 border-l border-gray-200"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {draftToDelete && (
                <ConfirmModal
                    title="Delete draft?"
                    message={`This will permanently delete the draft for ${draftToDelete.companyName || draftToDelete.customerName || 'this customer'} (${draftToDelete.visitDate}). This action cannot be undone.`}
                    confirmLabel={isDeleting ? 'Deleting…' : 'Delete'}
                    cancelLabel="Cancel"
                    variant="danger"
                    onConfirm={handleConfirmDelete}
                    onCancel={() => !isDeleting && setDraftToDelete(null)}
                />
            )}
        </>
    );
}
